import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { REGISTRY_STORAGE } from '../../registry-storage.token';
import { StorageInterface } from '../../interfaces/storage.interface';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface IPaginationItem {
  pageNumber: number;
  current: boolean;
  hidden: boolean;
}

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss', './pagination-new-style.component.scss'],
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() upButton = false;
  @Input() targetPage = 1;
  @Input() upOnChange = false;
  @Input() upButtonText = 'Наверх';
  @Input() previewButtonText = 'Предыдущая';
  @Input() nextButtonText = 'Следующая';
  @Input() countItems = 0;
  @Input() newStyle = false;
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();

  change = true;
  pages: IPaginationItem[] = [];
  private _itemOnPage = 12;
  private unsubscribe$ = new Subject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(REGISTRY_STORAGE) private storage: StorageInterface,
    ) {
  }

  activePage = 1;
  totalPages = 0;

  ngOnInit(): void {
    this.storage.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((queryParams) => {
      if (this.change) {
        this.activePage = Number(queryParams.page) || 1;
        if (this.targetPage !== 1 && this.activePage === 1) {
          this.changePage(this.targetPage, true);
        }
      }
    });
  }

  ngOnChanges() {
    this.totalPages = Math.ceil(this.countItems / this._itemOnPage);
    this.activePage = Math.min(this.activePage, this.totalPages);
    if (this.totalPages === 0) {
      this.activePage = 1;
    }
  }

  // Method to change the page
  changePage(page: number, moveUp = this.upOnChange) {

    const newState = Object.assign({}, this.storage.currentState(), { page: page.toString(10) });
    this.storage.changeState(newState);

    this.activePage = page;
    this.pageChange.emit(this.activePage);
    if (moveUp) {
      this.up();
    }
  }

  // Methods to handle next and previous
  nextPage() {
    if (this.activePage < this.totalPages) {
      this.changePage(this.activePage + 1);
    }
  }

  prevPage() {
    if (this.activePage > 1) {
      this.changePage(this.activePage - 1);
    }
  }

  /**
   * Количество страниц
   */
  get countPages(): number {
    return Math.ceil(this.countItems / this._itemOnPage);
  }

  /**
   * Поднять scroll
   */
  up(): void {
    const navbars = (this.document as Document).getElementsByTagName('app-header-top');
    for (let i = 0; i < navbars.length; i++) {
      const navbar = navbars.item(i);
      (navbar as HTMLDivElement).scrollIntoView({ behavior: 'smooth' });
    }
  }

  hasNextPage(): boolean {
    return this.countItems > (this._itemOnPage * this.activePage);
  }

  hasPreviousPage(): boolean {
    return this._itemOnPage * (this.activePage - 1) > 0;
  }

  preparePagination(): IPaginationItem[] {
    const pages: IPaginationItem[] = [];
    const pageCount = Math.ceil(this.countItems / this._itemOnPage);

    if (pageCount < 1) {
      return pages;
    }

    pages.push({
      pageNumber: 1,
      current: this.activePage === 1,
      hidden: false,
    });//Ставим первую страницу

    for (let i = 2; i < pageCount; i++) {
      if (i >= this.firstButtonNumber && pages.length < 7) {
        pages.push({
          pageNumber: i,
          current: this.activePage === i,
          hidden: (((i - this.activePage) > 2  && i > 5)  || ((this.activePage - i) > 1 && this.activePage > 3)),
        });
      }
    }
    pages.push({
      pageNumber: pageCount,
      current: this.activePage === pageCount,
      hidden: false,
    });//Ставим последнюю страницу

    return pages;
  }

  /**
   * Первый номер кнопки
   */
  get firstButtonNumber(): number {
    switch (this.countPages) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
        return 1;
      default:
        if (this.activePage > 3) {
          switch (this.countPages - this.activePage) {
            case 0:
            case 1:
            case 2:
              return this.countPages - 4;
            default:
              return this.activePage - 2;
          }
        } else {
          return 1;
        }
    }
  }
}
